import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { S } from './AdminUsers.styles';
import { Loading } from '../../components/Loading';
import { Input } from '../../components/Input';
import { useAuth } from '../../traits/hooks/useAuth';
import { useGetAdminUsers } from '../../traits/api/adminUsers/useGetAdminUsers';
import { useCreateAdminUser } from '../../traits/api/adminUsers/useCreateAdminUser';
import { useDeleteAdminUser } from '../../traits/api/adminUsers/useDeleteAdminUser';
import { useUpdateAdminUserRoles } from '../../traits/api/adminUsers/useUpdateAdminUserRoles';
import {
  AdminUser,
  ASSIGNABLE_ADMIN_ROLE_OPTIONS,
  AssignableAdminRole,
} from '../../traits/api/adminUsers/adminUsers.types';

const ROLE_LABELS: Record<string, string> = {
  'team-lead': 'Teamledare',
  inspector: 'Kontrollant',
  'safety-coordinator': 'Säkerhetssamordnare',
};

const RoleMultiSelect = ({
  selected,
  onChange,
}: {
  selected: AssignableAdminRole[];
  onChange: (roles: AssignableAdminRole[]) => void;
}) => {
  const toggleRole = (role: AssignableAdminRole) => {
    if (selected.includes(role)) {
      onChange(selected.filter((r) => r !== role));
      return;
    }
    onChange([...selected, role]);
  };

  return (
    <S.Field>
      <S.Label>Roller</S.Label>
      <S.CheckboxGroup>
        {ASSIGNABLE_ADMIN_ROLE_OPTIONS.map(({ value, label }) => (
          <S.CheckboxRow key={value}>
            <input
              type="checkbox"
              checked={selected.includes(value)}
              onChange={() => toggleRole(value)}
            />
            {label}
          </S.CheckboxRow>
        ))}
      </S.CheckboxGroup>
    </S.Field>
  );
};

export const AdminUsers: React.FC = () => {
  const { canManageAdminUsers } = useAuth();
  const {
    data: users,
    isLoading,
    error,
  } = useGetAdminUsers({
    enabled: canManageAdminUsers,
  });
  const createAdminUser = useCreateAdminUser();
  const deleteAdminUser = useDeleteAdminUser();
  const updateAdminUserRoles = useUpdateAdminUserRoles();

  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [createRoles, setCreateRoles] = useState<AssignableAdminRole[]>([
    'team-lead',
  ]);
  const [editRoles, setEditRoles] = useState<AssignableAdminRole[]>([]);

  const resetCreateForm = () => {
    setEmail('');
    setPassword('');
    setCreateRoles(['team-lead']);
  };

  const onCreate = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (createRoles.length === 0) return;

    await createAdminUser.mutateAsync({
      email: email.trim(),
      password,
      roles: createRoles,
    });
    resetCreateForm();
    setCreateOpen(false);
  };

  const onEdit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!editUser || editRoles.length === 0) return;

    await updateAdminUserRoles.mutateAsync({
      userId: editUser.userId,
      roles: editRoles,
    });
    setEditUser(null);
  };

  const openEdit = (user: AdminUser) => {
    setEditUser(user);
    setEditRoles(
      user.roles.filter((role): role is AssignableAdminRole =>
        ASSIGNABLE_ADMIN_ROLE_OPTIONS.some((option) => option.value === role)
      )
    );
  };

  if (!canManageAdminUsers) {
    return (
      <S.Container>
        <S.H6>Du har inte behörighet att se denna sida</S.H6>
      </S.Container>
    );
  }

  if (isLoading) {
    return (
      <S.Container>
        <S.H3>Administratörer</S.H3>
        <Loading />
      </S.Container>
    );
  }

  if (error) {
    return (
      <S.Container>
        <S.H3>Administratörer</S.H3>
        <S.H6>Någonting gick fel, försök igen senare.</S.H6>
      </S.Container>
    );
  }

  return (
    <S.Container>
      <S.Header>
        <S.H3>Administratörer</S.H3>
        <S.Button type="button" onClick={() => setCreateOpen(true)}>
          Ny administratör
        </S.Button>
      </S.Header>

      <Dialog.Root
        open={createOpen}
        onOpenChange={(next) => {
          setCreateOpen(next);
          if (!next) resetCreateForm();
        }}
      >
        <Dialog.Portal>
          <S.Overlay>
            <S.Content>
              <S.ContentHeader>
                <Dialog.Title asChild>
                  <S.H4>Skapa administratör</S.H4>
                </Dialog.Title>
                <S.CloseButton asChild>
                  <button type="button" aria-label="Stäng">
                    <S.Icon src="Close" width={20} height={20} />
                  </button>
                </S.CloseButton>
              </S.ContentHeader>

              <S.Form onSubmit={onCreate}>
                <Input
                  label="E-post"
                  type="email"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  required
                />
                <Input
                  label="Lösenord"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(ev) => setPassword(ev.target.value)}
                  required
                  minLength={8}
                />
                <RoleMultiSelect
                  selected={createRoles}
                  onChange={setCreateRoles}
                />
                <S.Actions>
                  <S.Button
                    $outlined
                    type="button"
                    onClick={() => setCreateOpen(false)}
                  >
                    Avbryt
                  </S.Button>
                  <S.Button
                    type="submit"
                    disabled={
                      createAdminUser.isLoading || createRoles.length === 0
                    }
                  >
                    Skapa
                  </S.Button>
                </S.Actions>
              </S.Form>
            </S.Content>
          </S.Overlay>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root
        open={Boolean(editUser)}
        onOpenChange={(next) => {
          if (!next) setEditUser(null);
        }}
      >
        <Dialog.Portal>
          <S.Overlay>
            <S.Content>
              <S.ContentHeader>
                <Dialog.Title asChild>
                  <S.H4>Redigera roller</S.H4>
                </Dialog.Title>
                <S.CloseButton asChild>
                  <button type="button" aria-label="Stäng">
                    <S.Icon src="Close" width={20} height={20} />
                  </button>
                </S.CloseButton>
              </S.ContentHeader>

              <S.Form onSubmit={onEdit}>
                <S.B2>{editUser?.email ?? editUser?.userId}</S.B2>
                <RoleMultiSelect selected={editRoles} onChange={setEditRoles} />
                <S.Actions>
                  <S.Button
                    $outlined
                    type="button"
                    onClick={() => setEditUser(null)}
                  >
                    Avbryt
                  </S.Button>
                  <S.Button
                    type="submit"
                    disabled={
                      updateAdminUserRoles.isLoading || editRoles.length === 0
                    }
                  >
                    Spara
                  </S.Button>
                </S.Actions>
              </S.Form>
            </S.Content>
          </S.Overlay>
        </Dialog.Portal>
      </Dialog.Root>

      <S.TableContainer>
        <S.Table>
          <thead>
            <tr>
              <S.Th>Namn</S.Th>
              <S.Th>E-post</S.Th>
              <S.Th>Roller</S.Th>
              <S.Th>Åtgärder</S.Th>
            </tr>
          </thead>
          <tbody>
            {(users ?? []).map((user) => (
              <tr key={user.userId}>
                <S.Td>{user.name || '—'}</S.Td>
                <S.Td>{user.email || '—'}</S.Td>
                <S.Td>
                  {user.roles.map((r) => ROLE_LABELS[r] ?? r).join(', ')}
                </S.Td>
                <S.Td>
                  <S.RowActions>
                    <S.Button
                      $outlined
                      type="button"
                      onClick={() => openEdit(user)}
                    >
                      Redigera
                    </S.Button>
                    <S.Button
                      $outlined
                      type="button"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Ta bort ${user.email ?? user.userId}?`
                          )
                        ) {
                          deleteAdminUser.mutate(user.userId);
                        }
                      }}
                    >
                      Ta bort
                    </S.Button>
                  </S.RowActions>
                </S.Td>
              </tr>
            ))}
          </tbody>
        </S.Table>
        {(users ?? []).length === 0 && (
          <S.B2>Inga administratörer hittades.</S.B2>
        )}
      </S.TableContainer>
    </S.Container>
  );
};
